<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import CsatMetricCard from './CsatMetricCard.vue';
import CsatRatingDistribution from './CsatRatingDistribution.vue';

const props = defineProps({
  targetScore: { type: Number, default: null },
});

const metrics = useMapGetter('csat/getMetrics');
const ratingPercentage = useMapGetter('csat/getRatingPercentage');
const ratingCount = useMapGetter('csat/getRatingCount');
// Média das notas (escala 1-5) - agora é a métrica principal,
// consistente com a meta que também é float na mesma escala.
const averageRating = useMapGetter('csat/getAverageRating');
const responseRate = useMapGetter('csat/getResponseRate');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingMetrics);

const responseCount = computed(() =>
  metrics.value.totalResponseCount
    ? metrics.value.totalResponseCount.toLocaleString()
    : '0'
);

const formatPercent = value => (value ? `${value}%` : '0%');

// Comparação direta na escala nativa 1-5.
//   verde: atinge a meta
//   amarelo: dentro de 0.5 ponto abaixo
//   vermelho: mais de 0.5 ponto abaixo
const targetStatus = computed(() => {
  if (!props.targetScore || averageRating.value == null) return null;
  const score = Number(averageRating.value) || 0;
  const target = Number(props.targetScore);
  if (score >= target) {
    return {
      color: 'text-n-grass-11',
      icon: 'i-lucide-check',
      label: `Meta atingida (${target.toFixed(1)})`,
    };
  }
  if (score >= target - 0.5) {
    return {
      color: 'text-n-amber-11',
      icon: 'i-lucide-trending-up',
      label: `Próximo da meta (${target.toFixed(1)})`,
    };
  }
  return {
    color: 'text-n-ruby-11',
    icon: 'i-lucide-trending-down',
    label: `Abaixo da meta (${target.toFixed(1)})`,
  };
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex sm:flex-row flex-col w-full gap-4 sm:gap-14 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <CsatMetricCard
        :label="$t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
        :tooltip="$t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
        :value="responseCount"
        :is-loading="isLoading"
      />

      <div class="w-full sm:w-px bg-n-strong" />

      <div class="flex-1">
        <CsatMetricCard
          label="Média das notas"
          tooltip="Média ponderada das respostas na escala de 1 a 5."
          :value="`${averageRating || '0.00'} / 5`"
          :is-loading="isLoading"
        />
        <p
          v-if="targetStatus && !isLoading"
          class="mt-1.5 inline-flex items-center gap-1 text-xs"
          :class="targetStatus.color"
        >
          <i :class="targetStatus.icon" class="size-3.5" />
          {{ targetStatus.label }}
        </p>
      </div>

      <div class="w-full sm:w-px bg-n-strong" />

      <CsatMetricCard
        :label="$t('CSAT_REPORTS.METRIC.RESPONSE_RATE.LABEL')"
        :tooltip="$t('CSAT_REPORTS.METRIC.RESPONSE_RATE.TOOLTIP')"
        :value="formatPercent(responseRate)"
        :is-loading="isLoading"
      />
    </div>

    <CsatRatingDistribution
      :rating-percentage="ratingPercentage"
      :rating-count="ratingCount"
      :total-response-count="metrics.totalResponseCount"
      :is-loading="isLoading"
    />
  </div>
</template>
